// Playbook field coordinate mapping (034) — the one place document
// coordinates (never pixels) convert to/from the fixed logical SVG
// viewBox every field/marker component renders into. Kept separate from
// any React component so it stays a pure, reusable function — this is
// exactly the "renderer" logic a future read-only Film Study view will
// need to reuse verbatim, per the project's own renderer/editor
// separation requirement. (VISUAL_RADIUS is imported below purely as a
// number for the LOS-boundary geometry functions near the bottom of
// this file — it adds no React dependency of its own.)
import { VISUAL_RADIUS } from './PlaybookMarker';
//
// Document coordinates:
//   x — normalized lateral position, centered at 0, range roughly -1..1
//       (0 = formation/ball centerline). Centered specifically so a
//       future mirror is the trivial transform x' = -x.
//   y — yards relative to the Line of Scrimmage. 0 = LOS. Negative =
//       the ACTIVE/AUTHORING unit's own backfield (rendered toward the
//       bottom of the screen, since the active unit is always bottom-
//       facing-up). Positive = downfield from the active unit's
//       perspective (rendered toward the top). This is intentionally
//       neutral — nothing here encodes "offense" or "defense"; that
//       classification lives only in the formation's own unit_type.
//
// The logical SVG viewBox is a fixed VIEW_WIDTH x VIEW_HEIGHT units —
// CSS handles actual on-screen scaling (width: 100%, height: auto), so
// this mapping never depends on real device pixel dimensions. These two
// constants are the ONLY thing Phase A.1's wider/landscape field change
// touches: a real field is wider than it is deep, so the viewBox is a
// landscape rectangle (was a tall 400x600 portrait rectangle, which read
// as a narrow phone-sized board). This is purely an internal drawing
// unit change — the football-relative x/y values stored on every
// marker (below) are completely unaffected by it, and every already-
// saved formation renders correctly against the new constants with no
// data migration.
export const VIEW_WIDTH = 400;
export const VIEW_HEIGHT = 240;

// Small side margins so markers/lines never sit flush against the SVG's
// own edge.
const SIDE_MARGIN = 24;

export function fieldToScreen(fieldConfig, x, y) {
  const beforeLos = fieldConfig?.before_los_yd ?? 15;
  const afterLos = fieldConfig?.after_los_yd ?? 15;
  const totalYards = beforeLos + afterLos || 1;

  const usableWidth = VIEW_WIDTH - SIDE_MARGIN * 2;
  const px = SIDE_MARGIN + ((x + 1) / 2) * usableWidth;
  const py = (VIEW_HEIGHT * (afterLos - y)) / totalYards;

  return { px, py };
}

export function screenToField(fieldConfig, px, py) {
  const beforeLos = fieldConfig?.before_los_yd ?? 15;
  const afterLos = fieldConfig?.after_los_yd ?? 15;
  const totalYards = beforeLos + afterLos || 1;

  const usableWidth = VIEW_WIDTH - SIDE_MARGIN * 2;
  const x = ((px - SIDE_MARGIN) / usableWidth) * 2 - 1;
  const y = afterLos - (py / VIEW_HEIGHT) * totalYards;

  return { x, y };
}

/** Y position (screen px) of the Line of Scrimmage for a given field
 * config — used to draw the LOS line and to derive yard-line positions. */
export function losScreenY(fieldConfig) {
  return fieldToScreen(fieldConfig, 0, 0).py;
}

// The LOS is drawn as a thin visual BAND rather than a zero-width line,
// so a coach can read "Center hangs closest to the band, rest of the
// line sits just behind it" instead of every OL marker looking bisected
// by a single pixel-thin rule. RENDERING ONLY — y = 0 stays the one
// canonical LOS coordinate (still drawn as an exact line at its center,
// see FootballField.js); no stored document changes because of this.
// Half-width chosen to be noticeably present without dominating the
// field (roughly a football's own length, symbolically). This value
// now ALSO feeds the boundary geometry below — a marker's CENTER
// coordinate is not, by itself, what determines whether it looks
// onside/offside; its drawn CIRCLE (this band's half-width plus the
// marker's own visible radius) is what a coach actually sees.
export const LOS_BAND_HALF_YD = 0.15;

/** Screen-Y range of the LOS band's near (current-unit side) and far
 * (opponent side) edges, for the translucent band rect. */
export function losBandScreenRange(fieldConfig) {
  const { py: nearY } = fieldToScreen(fieldConfig, 0, -LOS_BAND_HALF_YD);
  const { py: farY } = fieldToScreen(fieldConfig, 0, LOS_BAND_HALF_YD);
  return { nearY, farY };
}

// ─────────────────────────────────────────────────────────────────────
// Marker-geometry-aware LOS boundaries — Formation Editor use only.
//
// A marker is a drawn CIRCLE, not a point: its front (toward-LOS) edge
// sits VISUAL_RADIUS viewBox-units ahead of its own center coordinate.
// "Does this player look onside?" therefore has to compare that front
// edge against the LOS band's edges, not just compare the center
// coordinate to y = 0. These two functions derive the deepest
// (least-negative) center-Y a marker may have without its own front
// edge crossing the relevant band edge — never a guessed constant.
//
// visualRadiusYards() converts VISUAL_RADIUS into yards for the CURRENT
// field_config, since yards-per-viewBox-unit depends on the field's own
// total-yard window (offense/defense's default ±15yd view vs. special
// teams' much larger one) — a fixed yard constant would only be correct
// for one of them.
export function visualRadiusYards(fieldConfig) {
  const beforeLos = fieldConfig?.before_los_yd ?? 15;
  const afterLos = fieldConfig?.after_los_yd ?? 15;
  const totalYards = beforeLos + afterLos || 1;
  const unitsPerYard = VIEW_HEIGHT / totalYards;
  return VISUAL_RADIUS / unitsPerYard;
}

/** The normal current-unit-side boundary: a marker's front edge may
 * TOUCH the band's near edge, never cross it. Applies to every marker
 * except the offensive Center (see centerBoundaryY). */
export function onLineBoundaryY(fieldConfig) {
  return -LOS_BAND_HALF_YD - visualRadiusYards(fieldConfig);
}

/** The Center-only forward allowance: the one deliberate exception, for
 * the player who controls/snaps the ball — the front edge may extend
 * all the way to (but not past) the band's FAR edge. Formation-editor
 * visual convention only; not a legality rule. */
export function centerBoundaryY(fieldConfig) {
  return LOS_BAND_HALF_YD - visualRadiusYards(fieldConfig);
}
